import { Button, Checkbox, Dialog } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import { EmailApi, EmailGridDto, EmailSearch } from "../../../../api";
import { EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { exportFile } from "../../../../utils";

import { FilePond } from "react-filepond";

export interface IEmailListPageProps {}

export const EmailListPage: React.FunctionComponent<IEmailListPageProps> = (props) => {
  const api = useApiService(EmailApi);
  const { t } = useTl();
  const history = useHistory();

  const [uploadedFiles, setUploadedFile] = React.useState<any[]>([]);
  const [isImporting, setIsImporting] = React.useState(false);
  const [importDialogOpened, setImportDialogOpened] = React.useState<boolean>();
  const importTranslationFile = React.useCallback(async () => {
    try {
      setIsImporting(true);

      if (uploadedFiles.length > 0) {
        await api.upload({ data: uploadedFiles[0].file, fileName: uploadedFiles[0].filename });
      }
      setImportDialogOpened(false);
    } catch (err) {
      console.error(err);
      showError(t(ETLCodes.ErrorWhileImportingTranslations));
    }
    setIsImporting(false);
  }, [api, t, uploadedFiles]);

  const exportExcel = React.useCallback(async () => {
    const fd = await api.export();
    exportFile(fd);
  }, [api]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: EmailGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.email}/${row.mailTemplateId}/detail/view`)}
            />
            <EditButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.email}/${row.mailTemplateId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Code),
        fieldName: "code",
      },
      {
        header: () => t(ETLCodes.Subject),
        fieldName: "subject",
      },
      {
        header: () => t(ETLCodes.Description),
        fieldName: "mailTemplateDescription",
      },
      {
        header: () => t(ETLCodes.HasFrContent),
        fieldName: "frContent",
        alignment: "center",
        render: (row: EmailGridDto) => <Checkbox checked={row.frContent} disabled />,
      },
      {
        header: () => t(ETLCodes.HasEnContent),
        fieldName: "enContent",
        alignment: "center",
        render: (row: EmailGridDto) => <Checkbox checked={row.enContent} disabled />,
      },
      {
        header: () => t(ETLCodes.HasNlContent),
        fieldName: "nlContent",
        alignment: "center",
        render: (row: EmailGridDto) => <Checkbox checked={row.enContent} disabled />,
      },
    ],
    [history, t]
  );

  const searchFunction = React.useCallback(
    (sObj) => {
      return api.baseSearch(EmailSearch.fromJS(sObj));
    },
    [api]
  );
  const exportFunction = React.useCallback((sObj) => api.exportXlsFile(EmailSearch.fromJS(sObj)), [api]);

  const getSearchCriterias = React.useCallback(() => api.getSearchCriterias(true), [api]);

  return (
    <>
      <SearchTablePage
        columns={columns}
        buttons={[
          {
            rightIcon: "export",
            text: t(ETLCodes.Export),
            onClick: exportExcel,
          },
          {
            rightIcon: "import",
            text: t(ETLCodes.Import),
            onClick: () => setImportDialogOpened(true),
          },
        ]}
        getCriteriasFunction={getSearchCriterias}
        searchFunction={searchFunction}
        exportFunction={exportFunction}
        sortKeys={{ code: "ASC" }}
        breadCrumbs={[{ text: t(ETLCodes.Emails) }]}
      />
      <Dialog
        isOpen={importDialogOpened}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        onClose={() => {
          setImportDialogOpened(false);
        }}
      >
        <FilePond
          files={uploadedFiles}
          allowMultiple={false}
          onupdatefiles={(fileitems) => setUploadedFile(fileitems)}
          labelIdle={t(ETLCodes.FilePondDragOrSelect)}
        />
        <Button
          text={t(ETLCodes.General_Save)}
          onClick={importTranslationFile}
          loading={isImporting}
          disabled={uploadedFiles.length === 0}
        />
      </Dialog>
    </>
  );
};
